// @flow

import React from 'react';
import _ from 'lodash';

import { pluralize } from '@zapier/common-utils';

import * as Account from 'app/entities/Account';
import * as EntityActions from 'app/entity/EntityActions';
import * as UserNotification from 'app/entities/UserNotification';
import Button from 'app/common/components/Button';
import Link from 'app/common/components/Link';
import buildEntityActions from 'app/entity/utils/buildEntityActions';
import buildEntityApi from 'app/entity/utils/buildEntityApi';
import buildEntitySelectors from 'app/entity/utils/buildEntitySelectors';
import { partialWithDefault } from 'app/common/CommonUtils';
import { sendFormData } from 'app/common/fetch';
import { PROCESSING, SUCCESS } from 'app/entity/utils/statuses';

import type { Dispatch, GetState } from 'app/common/types';

type FileOptions = {
  accountId: number,
  file: File,
};

type Id = number | string;

const IS_PROMPTING_IMPORT = 'isPromptingImport';
const SHOWING_IMPORT_ERRORS_FOR = 'showingImportErrorsFor';

export const type = 'zapImportJob';

export const api = buildEntityApi(type, '/api/v3/zaps/imports', importApi => {
  importApi.importZaps = ({ file, accountId }: FileOptions) => {
    const url = importApi.collectionEndpoint();
    const fileData = new FormData();
    fileData.append('zapfile', file);
    fileData.append('account_id', `${accountId}`);
    return sendFormData(url, fileData).then(data => {
      return _.get(data, 'statuses')
        ? {
            // Backend doesn't return an `id` so fake it.
            id: _.uniqueId('zap-import-job-'),
            ...data,
          }
        : data;
    });
  };
});

export const selectors = buildEntitySelectors(type, sel => {
  const hasStatus = (statusToCheck, { status }) => status === statusToCheck;
  const isFailure = _.partial(hasStatus, 'error');
  const isProcessing = _.partial(hasStatus, PROCESSING);
  const isSuccess = _.partial(hasStatus, SUCCESS);
  sel.statuses = partialWithDefault(sel.dataItem, ['statuses'], []);
  sel.folderId = partialWithDefault(sel.dataItem, [['folder', 'id']], null);
  sel.failures = entity => sel.statuses(entity).filter(isFailure);
  sel.statusesCount = entity => sel.statuses(entity).length;
  sel.failureCount = entity => sel.failures(entity).length;
  sel.processingCount = entity =>
    sel.statuses(entity).filter(isProcessing).length;
  sel.successCount = entity => sel.statuses(entity).filter(isSuccess).length;
  sel.isJobComplete = entity => {
    const statuses = sel.statuses(entity);
    return (
      sel.isFailure(entity) ||
      (statuses.length > 0 &&
        statuses.every(({ status }) => status !== PROCESSING))
    );
  };
  sel.all.isPromptingImport = partialWithDefault(
    sel.all.stateItem,
    [IS_PROMPTING_IMPORT],
    false
  );
  sel.all.showingImportErrorsFor = partialWithDefault(
    sel.all.stateItem,
    [SHOWING_IMPORT_ERRORS_FOR],
    null
  );
});

export const actions = buildEntityActions(
  type,
  selectors,
  api,
  entityActions => {
    entityActions.promptImport = () =>
      entityActions.updateCollectionStateItem(IS_PROMPTING_IMPORT, true);
    entityActions.unpromptImport = () =>
      entityActions.updateCollectionStateItem(IS_PROMPTING_IMPORT, false);

    entityActions.showImportErrorsFor = cid =>
      entityActions.updateCollectionStateItem(SHOWING_IMPORT_ERRORS_FOR, cid);
    entityActions.hideImportErrors = () =>
      entityActions.updateCollectionStateItem(SHOWING_IMPORT_ERRORS_FOR, null);

    // POSTs a file (should be JSON) via `api.importZaps` to create a job.
    entityActions.importZaps = (
      fileOptions: FileOptions,
      messages: ?{},
      id: Id
    ) => {
      return EntityActions.createEntity(
        type,
        () => fileOptions,
        api.importZaps,
        messages,
        // Not a real `id`, but `createEntity` expects it.
        id
      );
    };

    entityActions.notifyImportStatus = cid => (
      dispatch: Dispatch,
      getState: GetState
    ) => {
      const entity = selectors.all.entityByCid(cid, getState());
      // If the entity completely failed then nothing was imported
      // and something catastrophic happened, like a bad JSON file.
      if (selectors.isFailure(entity)) {
        dispatch(
          UserNotification.actions.buildForEntity(
            {
              title: 'Import Failed',
              message: selectors.statusMessage(entity),
            },
            cid
          )
        );
      } else {
        const folderId = selectors.folderId(entity);
        const failureCount = selectors.failureCount(entity);
        const statusesCount = selectors.statusesCount(entity);
        const successCount = selectors.successCount(entity);
        const importMessage = `Imported ${successCount} of ${statusesCount} ${pluralize(
          'Zap',
          statusesCount
        )}.`;
        const errorMessage =
          failureCount > 0 &&
          `${failureCount} ${pluralize(
            'Zap',
            failureCount
          )} couldn’t be imported.`;
        dispatch(
          UserNotification.actions.buildForEntity(
            {
              title: `Import Complete`,
              message: ({ onClick }) => (
                <span>
                  {importMessage}{' '}
                  <Link href={`/app/zaps/folder/${folderId}`} onClick={onClick}>
                    View on My Zaps
                  </Link>{' '}
                  {errorMessage && <br />}
                  {errorMessage}{' '}
                  {errorMessage && (
                    <Button
                      kind="as-colored-link"
                      onClick={() =>
                        dispatch(entityActions.showImportErrorsFor(cid))
                      }
                    >
                      See details
                    </Button>
                  )}
                </span>
              ),
            },
            cid
          )
        );
      }
    };

    entityActions.importAndNotify = (
      fileOptions: { file: File },
      messages: ?{}
    ) => (dispatch: Dispatch, getState: GetState) => {
      const tempId = _.uniqueId('zap-import-');
      dispatch(entityActions.createCollectionUiEntity({ id: tempId }));
      const origEntity = selectors.all.entity(tempId, getState());
      const cid = selectors.cid(origEntity);
      dispatch(
        UserNotification.actions.buildForEntity(
          {
            title: 'Uploading…',
            message: 'Uploading your Zaps…',
          },
          cid
        )
      );
      return dispatch(
        entityActions.importZaps(
          {
            ...fileOptions,
            accountId: Account.selectors.all.currentAccountId(getState()),
          },
          messages,
          tempId
        )
      ).then(() => {
        dispatch(entityActions.notifyImportStatus(cid));
      });
    };
  }
);
